/**
 * For the sales reports (used with inventoryReportMixin and periodReportMixin): the Customer filter and a money
 * trend chart of `summary.trend` ([{ date, ...fields }], by `summary.trend_unit`).
 * The component's `filters` hold `customer` besides the period and item filters.
 */
export const SALES_COLOR = '#FE9F43';
export const PROFIT_COLOR = '#28C76F';
export const RETURN_COLOR = '#EA5455';
export const COUNT_COLOR = '#2D6CDF';
export const COST_COLOR = '#9AA1A9';
export const PALETTE = ['#FE9F43', '#2D6CDF', '#28C76F', '#EA5455', '#13A89E', '#7367F0', '#E2136E', '#FFB547',
  '#092C4C', '#9AA1A9'];

/** Payment types (cash / bank / card / mobile banking / due) as the server names them. */
export const PAYMENT_ICONS = {
  cash: 'ti-cash', bank: 'ti-building-bank', card: 'ti-credit-card', mobile: 'ti-device-mobile', due: 'ti-clock-dollar',
};
export const PAYMENT_TONES = { cash: 'success', bank: 'info', card: 'warning', mobile: 'info', due: 'danger' };
export const PAYMENT_COLORS = { cash: '#28C76F', bank: '#2D6CDF', card: '#FE9F43', mobile: '#E2136E', due: '#EA5455' };

const compactFormat = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

/** Badge / bar tone of a gross margin %. */
export function marginTone(margin) {
  const value = Number(margin) || 0;
  if (value < 0) return 'danger';
  if (value < 15) return 'warning';
  return 'success';
}

export default {
  computed: {
    customerOptions() {
      return (this.options.customers || []).map((c) => {
        const parts = [c.name];
        if (c.phone && !/^0+$/.test(c.phone)) parts.push(c.phone);
        const bal = Number(c.balance) || 0;
        if (Math.abs(bal) > 0.005) parts.push(`Due ${this.formatMoney(Math.abs(bal))}`);
        return { id: c.id, text: parts.join(' · ') };
      });
    },

    cashierOptions() {
      return (this.options.cashiers || []).map((c) => ({ id: c.id, text: c.name }));
    },
  },

  watch: {
    'filters.customer'() { this.onFilterChange(); },
  },

  methods: {
    customerParams() {
      return this.filters.customer ? { customer: this.filters.customer } : {};
    },

    customerText() {
      return `Customer: ${this.optionName(this.options.customers || [], this.filters.customer, 'All')}`;
    },

    storeText() {
      return `Store: ${this.optionName(this.options.stores, this.storeId, '—')}`;
    },

    cashierText() {
      return `Sold by: ${this.optionName(this.options.cashiers || [], this.filters.cashier, 'All')}`;
    },

    compact(value) {
      return compactFormat.format(Number(value) || 0);
    },

    /**
     * A bar chart of money per label. series: [{ label, data, color, line?, count? }]; `horizontal` lays the bars
     * sideways (for long names), `stacked` stacks the bar series.
     */
    drawBars(name, canvas, labels, series, { horizontal = false, stacked = false } = {}) {
      if (!labels.length) return;
      const valueAxis = horizontal ? 'x' : 'y';
      const labelAxis = horizontal ? 'y' : 'x';
      const hasCount = series.some((s) => s.count);
      this.drawChart(name, canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: series.map((s) => ({
            label: s.label,
            data: s.data.map((v) => Number(v) || 0),
            backgroundColor: s.color,
            borderColor: s.color,
            ...(s.line || s.count
              ? { type: 'line', tension: 0.3, cubicInterpolationMode: 'monotone', pointRadius: 3, borderWidth: 2, order: 0 }
              : { borderRadius: 4, maxBarThickness: horizontal ? 14 : 22, order: 1 }),
            ...(s.count ? { yAxisID: 'count' } : {}),
          })),
        },
        options: {
          indexAxis: labelAxis,
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const s = series[ctx.datasetIndex];
                  return `${s.label}: ${s.count ? this.formatInt(ctx.raw) : this.formatMoney(ctx.raw)}`;
                },
              },
            },
          },
          scales: {
            [labelAxis]: {
              stacked,
              grid: { display: false },
              ticks: {
                font: { size: 10 },
                autoSkip: !horizontal,
                maxRotation: 0,
                ...(horizontal ? { callback(v) { const t = this.getLabelForValue(v); return t.length > 22 ? `${t.slice(0, 21)}…` : t; } } : {}),
              },
            },
            [valueAxis]: {
              stacked,
              beginAtZero: true,
              grid: { color: '#F0F2F5' },
              ticks: { font: { size: 10 }, callback: (v) => compactFormat.format(v) },
            },
            ...(hasCount ? {
              count: { position: 'right', beginAtZero: true, grid: { display: false }, ticks: { font: { size: 10 }, precision: 0 } },
            } : {}),
          },
        },
      });
    },

    /** A doughnut of shares: entries [{ label, value }]. */
    drawDoughnut(name, canvas, entries) {
      if (!entries.length) return;
      this.drawChart(name, canvas, {
        type: 'doughnut',
        data: {
          labels: entries.map((e) => e.label),
          datasets: [{
            data: entries.map((e) => Math.max(0, Number(e.value) || 0)),
            backgroundColor: entries.map((e, i) => e.color || PALETTE[i % PALETTE.length]),
            borderWidth: 2,
            borderColor: '#fff',
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '62%',
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${this.formatMoney(ctx.raw)}` } },
          },
        },
      });
    },

    /** series: [{ label, field, color, line?, count? }]; a `count` series is drawn as a line on its own axis. */
    drawMoneyTrend(canvas, series) {
      const trend = this.summary.trend;
      if (!trend.length) return;
      const hasCount = series.some((s) => s.count);
      this.drawChart('trend', canvas, {
        type: 'bar',
        data: {
          labels: trend.map((t) => this.trendLabel(t.date)),
          datasets: series.map((s) => ({
            label: s.label,
            data: trend.map((t) => Number(t[s.field]) || 0),
            backgroundColor: s.color,
            borderColor: s.color,
            ...(s.line || s.count
              ? { type: 'line', tension: 0.3, cubicInterpolationMode: 'monotone', pointRadius: 3, borderWidth: 2, order: 0 }
              : { borderRadius: 4, maxBarThickness: 18, order: 1 }),
            yAxisID: s.count ? 'count' : 'y',
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const s = series[ctx.datasetIndex];
                  return `${s.label}: ${s.count ? this.formatInt(ctx.raw) : this.formatMoney(ctx.raw)}`;
                },
              },
            },
          },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true } },
            y: {
              beginAtZero: true,
              grid: { color: '#F0F2F5' },
              ticks: { font: { size: 10 }, callback: (v) => compactFormat.format(v) },
            },
            ...(hasCount ? {
              count: {
                position: 'right',
                beginAtZero: true,
                grid: { display: false },
                ticks: { font: { size: 10 }, precision: 0 },
              },
            } : {}),
          },
        },
      });
    },
  },
};
